import { useEffect, useRef } from "react";
import type { WaUpdateEvent } from "@/types/whatsapp";

/**
 * Calls `onUpdate` whenever a WhatsApp conversation the user can see changes.
 * Uses the server's live stream; if that drops (proxy, network), polls every 15s until it reconnects.
 */
export function useWhatsAppUpdates(onUpdate: (e: WaUpdateEvent | null) => void, enabled = true) {
  const handler = useRef(onUpdate);
  handler.current = onUpdate;

  useEffect(() => {
    if (!enabled) return;
    let source: EventSource | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const startPolling = () => {
      if (!pollTimer) pollTimer = setInterval(() => handler.current(null), 15000);
    };
    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    };

    const connect = () => {
      if (closed || typeof EventSource === "undefined") return startPolling();
      source = new EventSource("/api/WhatsApp/Stream", { withCredentials: true });
      source.addEventListener("open", () => {
        stopPolling();
        handler.current(null); // catch up on anything missed while disconnected
      });
      source.addEventListener("update", (ev) => {
        try {
          handler.current(JSON.parse((ev as MessageEvent).data));
        } catch {
          handler.current(null);
        }
      });
      source.addEventListener("error", () => {
        source?.close();
        startPolling();
        retryTimer = setTimeout(connect, 30000);
      });
    };

    connect();
    return () => {
      closed = true;
      source?.close();
      stopPolling();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [enabled]);
}
